<template>
  <div class="douyin-rewrite-page studio-layout">
    <el-card shadow="never" class="intro-card">
      <div class="intro-row">
        <div class="intro-text">
          <p>
            选择已在
            <router-link to="/publish">发布中心</router-link>
            登录的抖音账号，粘贴视频链接后自动下载 → 字幕优先 / ASR 转写 → LLM 洗稿。
            任务进度见
            <router-link to="/content/tasks">任务队列</router-link>
            ，成稿见
            <router-link to="/content/library">文章管理</router-link>
            （平台标签：抖音）。
          </p>
          <div class="asr-status">
            <el-tag size="small" :type="asrReady ? 'success' : 'danger'">
              ASR：{{ asrProviderLabel }}
            </el-tag>
            <el-tag size="small" type="info" v-if="asrInfo.model">model: {{ asrInfo.model }}</el-tag>
            <el-tag size="small" :type="asrReady ? 'success' : 'warning'">
              {{ asrReady ? '已配置鉴权' : '未配置 ASR（与 B 站洗稿共用 bilibili_rewrite.asr）' }}
            </el-tag>
          </div>
        </div>
      </div>
    </el-card>

    <el-card shadow="never" class="form-card">
      <el-form label-width="120px">
        <el-form-item label="抖音账号" required>
          <div class="account-row">
            <el-select
              v-model="accountId"
              placeholder="选择已登录账号"
              filterable
              style="width: 360px"
              :loading="loadingAccounts"
              :disabled="!douyinAccounts.length"
            >
              <el-option
                v-for="a in douyinAccounts"
                :key="a.account_id"
                :label="accountLabel(a)"
                :value="a.account_id"
              >
                <div class="account-option">
                  <span class="account-option-name">{{ a.display_name || a.account_id }}</span>
                  <el-tag size="small" :type="a.has_cookie ? 'success' : 'info'">
                    {{ a.has_cookie ? '已登录' : '未登录' }}
                  </el-tag>
                </div>
              </el-option>
            </el-select>
            <el-button :loading="loadingAccounts" @click="loadAccounts">刷新账号</el-button>
            <el-button type="primary" link @click="goPublish">去发布中心登录</el-button>
          </div>
          <div v-if="!loadingAccounts && !douyinAccounts.length" class="hint warn">
            暂无抖音账号。请先在发布中心完成抖音登录，再回来选择账号。
          </div>
          <div v-else-if="selectedAccount && !selectedAccount.has_cookie" class="hint warn">
            当前账号未检测到 Cookie，请先在发布中心重新登录。
          </div>
        </el-form-item>

        <el-form-item label="视频链接">
          <el-input
            v-model="form.videoUrl"
            type="textarea"
            :rows="3"
            placeholder="例如：https://www.douyin.com/video/7xxx 或 v.douyin.com 短链；多个链接换行分隔"
          />
          <div class="hint">
            将使用所选账号 Cookie 下载视频。有字幕时直接用字幕；否则下载音轨走 ASR 转写。
          </div>
        </el-form-item>

        <el-form-item label="或粘贴文稿">
          <el-input
            v-model="form.originalContent"
            type="textarea"
            :rows="8"
            placeholder="若已有文案/字幕，可跳过下载直接洗稿（生成 3 个版本）"
          />
          <div class="char-count">{{ form.originalContent.length }} 字</div>
        </el-form-item>

        <el-collapse v-model="optionalOpen" class="optional-collapse">
          <el-collapse-item title="洗稿选项（可选）" name="optional">
            <el-form-item label="洗稿强度">
              <el-radio-group v-model="form.intensity">
                <el-radio value="light">轻度改写</el-radio>
                <el-radio value="medium">中度改写</el-radio>
                <el-radio value="deep">深度改写</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="补充要求">
              <el-input
                v-model="form.customPrompt"
                type="textarea"
                :rows="2"
                placeholder="例如：口语化、短句、适合抖音口播"
              />
            </el-form-item>
          </el-collapse-item>
        </el-collapse>

        <el-form-item>
          <el-button
            type="primary"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            {{ submitLabel }}
          </el-button>
          <el-button @click="resetForm">清空</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card v-if="versions.length" class="result-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span>洗稿结果（粘贴文稿模式）</span>
          <el-tag type="success" size="small">已生成 {{ versions.length }} 个版本</el-tag>
        </div>
      </template>
      <el-tabs v-model="activeTab">
        <el-tab-pane
          v-for="v in versions"
          :key="v.version_index"
          :label="`版本 ${v.version_index}`"
          :name="String(v.version_index)"
        >
          <div class="version-toolbar">
            <span class="version-title">{{ v.title }}</span>
            <el-button size="small" @click="copyContent(v.content)">复制正文</el-button>
          </div>
          <el-input v-model="v.content" type="textarea" :rows="16" readonly />
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { publishV2Api, rewriteApi, douyinRewriteApi } from '../api'

const STORAGE_KEY = 'douyin_rewrite_account_id'

const router = useRouter()
const loadingAccounts = ref(false)
const submitting = ref(false)
const accounts = ref([])
const accountId = ref('')
const optionalOpen = ref([])
const activeTab = ref('1')
const versions = ref([])
const asrInfo = ref({})

const form = reactive({
  videoUrl: '',
  originalContent: '',
  intensity: 'medium',
  customPrompt: '口语化、短句，适合抖音口播',
})

const douyinAccounts = computed(() =>
  accounts.value.filter((a) => a.platform === 'douyin'),
)

const selectedAccount = computed(() =>
  douyinAccounts.value.find((a) => a.account_id === accountId.value) || null,
)

const videoUrls = computed(() =>
  (form.videoUrl || '').split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
)

const hasVideoInput = computed(() => videoUrls.value.length > 0)
const hasTextInput = computed(() => Boolean(form.originalContent.trim()))

const asrReady = computed(() => Boolean(asrInfo.value?.has_key))
const asrProviderLabel = computed(() => asrInfo.value?.provider || 'volc_auc_turbo')

const canSubmit = computed(() => {
  if (!accountId.value || !selectedAccount.value?.has_cookie) return false
  return hasTextInput.value || hasVideoInput.value
})

const submitLabel = computed(() => {
  if (hasVideoInput.value && !hasTextInput.value) {
    return `提交视频洗稿（${videoUrls.value.length} 条）`
  }
  if (hasTextInput.value && !hasVideoInput.value) {
    return '开始洗稿（生成 3 个版本）'
  }
  if (hasVideoInput.value && hasTextInput.value) {
    return '提交视频洗稿（已填文稿时将优先走视频下载）'
  }
  return '开始洗稿'
})

const accountLabel = (a) => {
  const name = a.display_name || a.account_id
  const status = a.has_cookie ? '已登录' : '未登录'
  return `${name} (${a.account_id}) · ${status}`
}

const loadAsrStatus = async () => {
  try {
    asrInfo.value = await douyinRewriteApi.asrStatus()
  } catch {
    asrInfo.value = {}
  }
}

const loadAccounts = async () => {
  loadingAccounts.value = true
  try {
    const res = await publishV2Api.listAccounts({ platform: 'douyin' })
    accounts.value = res.items || []
    const saved = localStorage.getItem(STORAGE_KEY) || ''
    const exists = accounts.value.some((a) => a.account_id === saved)
    if (exists) {
      accountId.value = saved
    } else {
      const loggedIn = accounts.value.find((a) => a.has_cookie)
      accountId.value = loggedIn?.account_id || accounts.value[0]?.account_id || ''
    }
  } catch (e) {
    ElMessage.error(e?.message || '加载抖音账号失败')
  } finally {
    loadingAccounts.value = false
  }
}

watch(accountId, (id) => {
  if (id) localStorage.setItem(STORAGE_KEY, id)
})

const buildStyleHint = () => {
  const parts = [form.customPrompt.trim()].filter(Boolean)
  return parts.join('；')
}

const submitVideoRewrite = async () => {
  const res = await douyinRewriteApi.submit({
    account_id: accountId.value,
    inputs: videoUrls.value,
    style_hint: buildStyleHint(),
    intensity: form.intensity,
  })
  ElMessage.success(`已提交任务 #${res.task_id}，请到任务队列查看进度`)
  router.push('/content/tasks')
  return res
}

const submitTextRewrite = async () => {
  const custom = [
    form.customPrompt.trim(),
    `目标平台：抖音；使用账号：${accountId.value}`,
  ].filter(Boolean).join('；')

  const res = await rewriteApi.generate({
    original_content: form.originalContent.trim(),
    intensity: form.intensity,
    length: 'keep',
    keywords: '',
    custom_prompt: custom,
    save_to_db: true,
  })
  versions.value = res.versions || []
  if (versions.value.length) {
    activeTab.value = String(versions.value[0].version_index)
    ElMessage.success(`已生成 ${versions.value.length} 个版本`)
  } else {
    ElMessage.error('未能生成任何版本')
  }
}

const handleSubmit = async () => {
  if (!accountId.value) {
    ElMessage.warning('请先选择抖音账号')
    return
  }
  if (!selectedAccount.value?.has_cookie) {
    ElMessage.warning('所选账号未登录，请先去发布中心登录')
    return
  }

  if (!hasVideoInput.value && !hasTextInput.value) {
    ElMessage.warning('请填写视频链接或粘贴文稿')
    return
  }

  submitting.value = true
  versions.value = []
  try {
    if (hasVideoInput.value) {
      await submitVideoRewrite()
    } else {
      await submitTextRewrite()
    }
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || e?.message || '提交失败')
  } finally {
    submitting.value = false
  }
}

const resetForm = () => {
  form.videoUrl = ''
  form.originalContent = ''
  form.customPrompt = '口语化、短句，适合抖音口播'
  versions.value = []
}

const copyContent = async (text) => {
  try {
    await navigator.clipboard.writeText(text || '')
    ElMessage.success('已复制')
  } catch {
    ElMessage.error('复制失败')
  }
}

const goPublish = () => router.push('/publish')

onMounted(() => {
  loadAccounts()
  loadAsrStatus()
})
</script>

<style scoped>
.douyin-rewrite-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.intro-card :deep(.el-card__body) {
  padding: 16px 20px;
}
.intro-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.intro-text p {
  margin: 0;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}
.intro-text a {
  color: #409eff;
  text-decoration: none;
}
.asr-status {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.form-card :deep(.el-card__body) {
  padding: 20px;
}
.account-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.account-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.account-option-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hint {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}
.hint.warn {
  color: #e6a23c;
}
.char-count {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  text-align: right;
}
.optional-collapse {
  margin-bottom: 12px;
  border: none;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.result-card {
  margin-top: 4px;
}
.version-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
}
.version-title {
  font-weight: 600;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
